// lineChart.ts

type Padding = { top: number; right: number; bottom: number; left: number }

export interface Series {
	name: string
	color: string // CSS color
	data: number[]
}

export interface ChartConfig {
	width?: number
	height?: number
	padding?: Padding
	title?: string
	xLabel?: string
	yLabel?: string
	xCategories: string[] // ex) ["데이터 1", ...]
	series: Series[] // ex) [{ name, color, data }]
	gridLines?: number // horizontal grid count
	showArea?: boolean // fill under lines
}

export function renderBarChartSVG(mountTarget: string | HTMLElement, cfg: ChartConfig): void {
	const mount = typeof mountTarget === 'string' ? document.querySelector<HTMLElement>(mountTarget) : mountTarget
	if (!mount) throw new Error('Mount element not found')

	const {
		width = 960,
		height = 400,
		padding = { top: 40, right: 40, bottom: 60, left: 46 },
		title = 'Bar chart name',
		xLabel = '(x축 레이블)',
		yLabel = '(y축 레이블)',
		xCategories,
		series,
		gridLines = 6,
		showArea = true,
	} = cfg

	// 초기화
	mount.innerHTML = ''
	mount.style.position = 'relative'

	const svgNS = 'http://www.w3.org/2000/svg'
	const svg = createEl('svg', {
		width: String(width),
		height: String(height),
		viewBox: `0 0 ${width} ${height}`,
		style: 'background:#fff;border-radius:4px;',
	})

	const root = createEl('g')
	svg.appendChild(root)

	// 제목
	root.appendChild(
		createEl(
			'text',
			{
				x: '20',
				y: '26',
				fill: '#6B6E79',
				'font-size': '20',
				'font-weight': '700',
				'font-family': 'ui-sans-serif, -apple-system, "Noto Sans KR", Arial',
			},
			title,
		),
	)

	// 플롯 영역
	const plotX = padding.left
	const plotY = padding.top
	const plotW = width - padding.left - padding.right
	const plotH = height - padding.top - padding.bottom

	const rectHeight = plotY + plotH + 50

	// 스케일
	const allY = series.flatMap(s => s.data)
	const yMaxData = Math.max(allY.length ? Math.max(...allY) : 10, 1)
	const yMax = niceMax(yMaxData)
	const yMin = 0

	const maxLen = Math.max(...series.map(s => s.data.length))
	const xCats = cfg.xCategories && cfg.xCategories.length ? cfg.xCategories : Array.from({ length: maxLen }, (_, i) => `데이터 ${i + 1}`)
	const xCount = Math.max(xCategories.length, 1) + 1
	const xStep = plotW / Math.max(xCount - 1, 1)
	const xScale = (i: number) => plotX + i * xStep
	const yScale = (v: number) => plotY + 65 + (yMax - v) * ((plotH - 60) / (yMax - yMin))
	const xsScale = (x: number, i: number, s: number) => x + s * i

	// 수평 그리드 + y축 눈금
	const effectiveGridLines = Math.min(gridLines, yMax)
	const grid = createEl('g')
	root.appendChild(grid)
	for (let i = 0; i <= effectiveGridLines; i++) {
		const v = yMax * (i / effectiveGridLines)
		const y = yScale(v)
		grid.appendChild(
			createEl('line', {
				x1: String(plotX),
				y1: String(y),
				x2: String(plotX + plotW),
				y2: String(y),
				stroke: '#DDE1E7',
				'stroke-dasharray': '4 4',
			}),
		)
		grid.appendChild(
			createEl(
				'text',
				{
					x: String(plotX - 10),
					y: String(y + 5),
					'text-anchor': 'end',
					fill: '#6B6E79',
					'font-size': '12',
					'font-family': 'ui-sans-serif, "Noto Sans KR", Arial',
				},
				String(Math.round(v)),
			),
		)
	}

	// 라벨
	root.appendChild(
		createEl(
			'text',
			{
				x: String(20),
				y: String(plotY + 48),
				fill: '#6B6E79',
				'font-size': '12',
				'font-weight': '600',
			},
			yLabel,
		),
	)

	root.appendChild(
		createEl(
			'text',
			{
				x: String(plotX + plotW),
				y: String(rectHeight),
				'text-anchor': 'end',
				fill: '#6B6E79',
				'font-size': '12',
				'font-weight': '600',
			},
			xLabel,
		),
	)

	// x축 카테고리 + 세로 보조선
	const xTicks = createEl('g')
	root.appendChild(xTicks)
	xCats.forEach((lab, i) => {
		const x = xScale(i)
		const xt = xScale(i + 1)
		xTicks.appendChild(
			createEl('line', {
				x1: String(x),
				y1: String(plotY + 65),
				x2: String(x),
				y2: String(rectHeight - 45),
				stroke: '#DDE1E7',
				'stroke-dasharray': '4 4',
			}),
		)
		if (i === xCats.length - 1) {
			const x = xScale(i + 1)
			xTicks.appendChild(
				createEl('line', {
					x1: String(x),
					y1: String(plotY + 65),
					x2: String(x),
					y2: String(rectHeight - 45),
					stroke: '#DDE1E7',
					'stroke-dasharray': '4 4',
				}),
			)
		}
		xTicks.appendChild(
			createEl(
				'text',
				{
					x: String(xt - (xt - x) / 2),
					y: String(plotY + plotH + 30),
					'text-anchor': 'middle',
					fill: '#111827',
					'font-size': '12',
					'font-family': 'ui-sans-serif, "Noto Sans KR", Arial',
				},
				lab,
			),
		)
	})

	// 범례
	const legend = createEl('g')
	root.appendChild(legend)
	let lx = 20
	const ly = 48
	series.forEach((s, idx) => {
		const group = createEl('g')
		legend.appendChild(group)
		group.appendChild(
			createEl('rect', {
				x: String(lx),
				y: String(ly - 10),
				width: '14',
				height: '14',
				rx: '3',
				fill: s.color,
			}),
		)
		group.appendChild(
			createEl(
				'text',
				{
					x: String(lx + 20),
					y: String(ly + 2),
					fill: '#374151',
					'font-size': '12',
				},
				s.name ?? `시리즈${idx + 1}`,
			),
		)
		lx += 90
	})

	// 테두리
	root.appendChild(
		createEl('line', {
			x1: String(plotX),
			y1: String(rectHeight - 45),
			x2: String(plotX + plotW),
			y2: String(rectHeight - 45),
			stroke: '#6B6E79',
		}),
	)

	mount.appendChild(svg)

	// ======= Tooltip =======
	const tooltip = document.createElement('div')
	tooltip.style.position = 'absolute'
	tooltip.style.zIndex = '10'
	tooltip.style.pointerEvents = 'none'
	tooltip.style.padding = '6px 8px'
	tooltip.style.background = '#111827'
	tooltip.style.color = '#fff'
	tooltip.style.borderRadius = '4px'
	tooltip.style.fontSize = '12px'
	tooltip.style.lineHeight = '1.2'
	tooltip.style.whiteSpace = 'nowrap'
	tooltip.style.boxShadow = '0 4px 12px rgba(0,0,0,0.18)'
	tooltip.style.opacity = '0'
	mount.appendChild(tooltip)

	const showTooltip = (html: string) => {
		tooltip.innerHTML = html
		tooltip.style.opacity = '1'
	}
	const moveTooltip = (e: MouseEvent) => {
		const r = mount.getBoundingClientRect()
		// 기본 위치: 커서 위쪽 약간
		let x = e.clientX - r.left + 8
		let y = e.clientY - r.top - 12
		// 경계 보정
		tooltip.style.left = '0px'
		tooltip.style.top = '-1000px'
		// 먼저 DOM에 그려진 뒤 bounding 계산
		const tw = tooltip.offsetWidth || 0
		const th = tooltip.offsetHeight || 0
		if (x + tw + 8 > r.width) x = r.width - tw - 8
		if (x < 4) x = 4
		if (y < 4) y = e.clientY - r.top + 16 // 위로 공간이 없으면 아래로
		if (y + th + 4 > r.height) y = r.height - th - 4
		tooltip.style.left = `${x}px`
		tooltip.style.top = `${y}px`
	}
	const hideTooltip = () => {
		tooltip.style.opacity = '0'
	}

	// ======= 내부 유틸 =======
	function createEl<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string> = {}, text?: string): SVGElementTagNameMap[K] {
		const el = document.createElementNS(svgNS, tag) as SVGElementTagNameMap[K]
		Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v))
		if (text != null) el.textContent = text
		return el
	}

	function niceMax(v: number): number {
		const pow = Math.pow(10, Math.floor(Math.log10(v)))
		const n = v / pow
		const step = n <= 2 ? 2 : n <= 5 ? 5 : 10
		return step * pow
	}

	type Pt = [number, number] | null
	const pointsBySeries: Pt[][] = series.map(s => Array.from({ length: xCount }, (_, i) => (typeof s.data[i] === 'number' ? [xScale(i), yScale(s.data[i] as number)] : null)))

	pointsBySeries.forEach((pts, si) => {
		const color = series[si].color
		pts.forEach((p: any, i: number) => {
			if (!p) return
			const x = xScale(i)
			const xt = xScale(i + 1)
			const xc = (xt - x) / series.length - 8
			const xs = xsScale(x, si, xc) + (8 * (si + 1)) / 1.2
			const rect = createEl('rect', {
				x: String(xs),
				y: String(p[1]),
				width: String(xc),
				height: String(rectHeight - 45 - p[1]),
				rx: '3',
				fill: color,
				class: 'animation--chart',
			})
			// Tooltip 데이터 및 이벤트
			const seriesName = series[si]?.name ?? `시리즈${si + 1}`
			const categoryLabel = xCats[i] ?? `데이터 ${i + 1}`
			const value = typeof series[si]?.data?.[i] === 'number' ? (series[si].data[i] as number) : 0
			;(rect as any).dataset.series = String(si)
			;(rect as any).dataset.categoryIndex = String(i)
			;(rect as any).dataset.value = String(value)
			rect.addEventListener('mouseenter', (e: MouseEvent) => {
				showTooltip(
					`<div style="font-weight:700;margin-bottom:2px;">${categoryLabel}</div><div>${seriesName}: <b>${value}</b></div>`,
				)
				moveTooltip(e)
			})
			rect.addEventListener('mousemove', (e: MouseEvent) => moveTooltip(e))
			rect.addEventListener('mouseleave', () => hideTooltip())
			root.appendChild(rect)
		})
	})
}

/* ================= 예시 호출 =================
import { renderLineChartSVG } from "./lineChart";

renderLineChartSVG("#chart", {
  title: "Line chart name",
  xLabel: "(x축 레이블)",
  yLabel: "(y축 레이블)",
  xCategories: ["데이터 1", "데이터 2", "데이터 3", "데이터 4", "데이터 5"],
  series: [
    { name: "라벨1번", color: "#9CA3AF", data: [10, 6, 2, 7, 9] },
    { name: "라벨2번", color: "#22C1D6", data: [3, 5, 4, 8, 6] },
    { name: "라벨3번", color: "#F4C542", data: [5, 6, 1.5, 2, 3.5] },
    { name: "라벨4번", color: "#A78BFA", data: [11, 5.8, 9, 8.5, 10] }
  ],
  showArea: true
});
================================================ */
